import type { ReactNode } from 'react';
import type React from 'react';
import { Suspense, useEffect, useRef, useState } from 'react';
import SectionLoadingSkeleton from '../Sections/SectionLoadingSkeleton';
import { gridPageMinHeightInPixels } from '../shared/gridConfig';
import { useActiveEditor } from '../GridDndEditor/TiptapEditor/ActiveEditorContext.tsx';

interface LazyLoadComponentProps {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: string;
  className?: string;
  sectionId: string;
  testId?: string;
}

const intersectionObserverRootMargin = '100px';

const LazyLoadComponent: React.FC<LazyLoadComponentProps> = ({
  children,
  sectionId,
  rootMargin = intersectionObserverRootMargin,
  minHeight = `${gridPageMinHeightInPixels}px`,
  className,
  testId,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { activeEditor, setActiveEditor } = useActiveEditor();

  // Keep a ref to the latest callback so the observer is never recreated just
  // because isVisible or activeEditor changed — those are stale-closure sources
  // that previously caused [options, ref] to re-run on every render.
  const callbackFnRef = useRef<((entries: IntersectionObserverEntry[]) => void) | null>(null);
  useEffect(() => {
    callbackFnRef.current = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      const isCurrentSectionHidden =
        isVisible && !entry.isIntersecting && activeEditor?.options.editorProps.attributes?.['sectionId'] === sectionId;
      if (isCurrentSectionHidden) {
        setActiveEditor(null);
      }
      setIsVisible(entry.isIntersecting);
    };
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => callbackFnRef.current?.(entries), {
      root: null,
      rootMargin,
    });
    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [rootMargin]);

  return (
    <div ref={ref} style={{ minHeight }} className={className} data-testid={testId}>
      {isVisible ? <Suspense fallback={<SectionLoadingSkeleton />}>{children}</Suspense> : <SectionLoadingSkeleton />}
    </div>
  );
};

export default LazyLoadComponent;
